// Catalog threading/sorting utilities.
// Pure functions: no DOM, no network, no global state.

export function effectiveStatus(s) {
  return s?.reblog || s;
}

// Mitra exposes the conversation root on each non-repost status. Repost
// wrappers have no conversation of their own, so callers pass effectiveStatus
// when they want the boosted status's root.
export function statusConversationRootId(status) {
  return status?.conversation?.root_id || null;
}

export function statusActivityKey(s) {
  if (!s) return { at: "", id: "" };
  return { at: s.created_at || "", id: s.id || "" };
}

export function compareActivityDesc(a, b) {
  if ((a.at || "") !== (b.at || "")) return (b.at || "").localeCompare(a.at || "");
  return (b.id || "").localeCompare(a.id || "");
}

export function compareActivityAsc(a, b) {
  if ((a.at || "") !== (b.at || "")) return (a.at || "").localeCompare(b.at || "");
  return (a.id || "").localeCompare(b.id || "");
}

export function resolveThreadRootId(status, byId) {
  if (!status?.id) return null;
  const declaredRootId = statusConversationRootId(status);
  if (declaredRootId) return declaredRootId;

  let cur = status;
  const seen = new Set();
  while (cur?.in_reply_to_id && !seen.has(cur.id)) {
    seen.add(cur.id);
    const curRootId = statusConversationRootId(cur);
    if (curRootId) return curRootId;
    const parentId = cur.in_reply_to_id;
    const parent = byId.get(parentId);
    if (!parent) return parentId;          // unknown ancestor; best-effort root
    cur = parent;
  }
  return cur?.id || status.id;
}

export function buildCatalogThreads(statuses) {
  // Group timeline activity by OP thread:
  // - replies bump their OP
  // - reposts/boosts of replies bump the reply's OP
  // - reposts/boosts of OPs bump that OP
  // Only OPs are renderable in catalog view.
  const byId = new Map();
  for (const row of statuses || []) {
    const st = effectiveStatus(row);
    if (st?.id) byId.set(st.id, st);
  }

  const threads = new Map();
  for (const row of statuses || []) {
    const target = effectiveStatus(row);
    if (!target?.id) continue;

    const rootId = resolveThreadRootId(target, byId);
    if (!rootId) continue;

    let thread = threads.get(rootId);
    if (!thread) {
      thread = {
        id: rootId,
        op: null,
        bump: { at: "", id: "" },
        activities: [],
      };
      threads.set(rootId, thread);
    }

    if (target.id === rootId && !target.in_reply_to_id) {
      thread.op = target;
    }

    const eventKey = statusActivityKey(row);
    if (compareActivityDesc(eventKey, thread.bump) < 0) {
      thread.bump = eventKey;
    }

    // Preserve actor-level bump reasons for the catalog hover summary. A
    // boost wrapper identifies the boosting account; an unwrapped descendant
    // identifies the replying account. The OP's original publication isn't a
    // bump reason and is intentionally omitted.
    if (row?.reblog) {
      thread.activities.push({
        id: row.id,
        at: row.created_at || "",
        type: "repost",
        account: row.account || null,
      });
    } else if (target.id !== rootId && target.in_reply_to_id) {
      thread.activities.push({
        id: target.id,
        at: target.created_at || "",
        type: "reply",
        account: target.account || null,
      });
    }
  }

  // Backfill OPs from known statuses (if the OP itself appeared in timeline).
  for (const thread of threads.values()) {
    if (!thread.op) {
      const maybeOp = byId.get(thread.id);
      if (maybeOp && !maybeOp.in_reply_to_id) thread.op = maybeOp;
    }
    if (thread.op && !thread.bump.at) {
      thread.bump = statusActivityKey(thread.op);
    }
    thread.activities.sort((a, b) => compareActivityDesc(a, b));
  }

  return Array.from(threads.values()).filter((t) => !!t.op);
}

// Collapse a direct-message timeline into one card per DM thread.
//
// Unlike buildCatalogThreads this never drops a message. A DM sent as a reply
// to a public or followers-only post resolves to a root that isn't in the
// timeline (only direct posts are), and a thread whose OP is missing would be
// filtered out entirely — which is exactly the DM that most needs showing. So
// messages are grouped by their resolved root id, and the card is the OLDEST
// message loaded for that root: the start of the DM exchange as far as the
// timeline can see it. Clicking through opens the real thread, ancestors and
// all. `bump` is the group's newest message, so the catalog can order by
// latest activity; the other messages become hover-overlay activities.
export function dmCatalogThreads(statuses) {
  const byId = new Map();
  for (const s of statuses || []) {
    if (s?.id) byId.set(s.id, s);
  }

  const groups = new Map();
  for (const s of statuses || []) {
    if (!s?.id) continue;
    const rootId = resolveThreadRootId(s, byId) || s.id;
    const key = statusActivityKey(s);
    let group = groups.get(rootId);
    if (!group) {
      groups.set(rootId, { op: s, bump: key, activities: [] });
      continue;
    }
    if (compareActivityAsc(key, statusActivityKey(group.op)) < 0) group.op = s;
    if (compareActivityDesc(key, group.bump) < 0) group.bump = key;
  }

  // Every message that isn't the card's own is a reply within the exchange.
  for (const s of statuses || []) {
    if (!s?.id) continue;
    const group = groups.get(resolveThreadRootId(s, byId) || s.id);
    if (!group || group.op.id === s.id) continue;
    group.activities.push({
      id: s.id,
      at: s.created_at || "",
      type: "reply",
      account: s.account || null,
    });
  }

  return Array.from(groups.values()).map((group) => {
    group.activities.sort((a, b) => compareActivityDesc(a, b));
    return { id: group.op.id, op: group.op, bump: group.bump, activities: group.activities };
  });
}

// "Flat" catalog: show every status as its own card, rather than collapsing
// activity to OP threads. A reblog wrapper is unwrapped to its target via
// effectiveStatus, so a boosted post shows the boosted content (matching how
// the /likes/ and /bkmks/ collection views render). Used by those collection
// boards (in server order) and by profiles with the "show replies and
// reposts" toggle — which needs each reply/repost as a standalone card whose
// click opens its own thread.
export function flatCatalogThreads(statuses) {
  return (statuses || [])
    .map((row) => {
      const op = effectiveStatus(row);
      if (!op?.id) return null;
      return { id: op.id, op, bump: statusActivityKey(row) };
    })
    .filter((t) => !!t?.op);
}

// Keep the user's spelling, but treat whitespace and case variants as one mute.
export function normalizeMutedWords(words) {
  if (!Array.isArray(words)) return [];
  const seen = new Set();
  return words.filter((word) => typeof word === "string")
    .map((word) => word.normalize("NFC").trim().replace(/\s+/g, " "))
    .filter((word) => {
      const key = word.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function filterCatalogThreads(threads, view, filterText, stripHTML, mutedWords = []) {
  let out = [...threads];

  // Match literal text in the OP only. Replies, account names and HTML
  // attributes must not hide an otherwise unrelated thread.
  const mutes = normalizeMutedWords(mutedWords).map((word) => word.toLowerCase());
  if (mutes.length) {
    out = out.filter((t) => {
      const op = t.op || {};
      const text = [op.content, op.title, op.summary, op.spoiler_text]
        .map((field) => stripHTML(field || "").normalize("NFC").replace(/\s+/g, " ").toLowerCase());
      return !mutes.some((word) => text.some((field) => field.includes(word)));
    });
  }

  // On profiles, keep only the viewed account's OPs — hydration/merge can
  // otherwise surface other users' posts. Skipped in flat mode (view.includeReplies):
  // there every card is the account's own activity (replies it authored, plus
  // reblogs whose unwrapped target is someone else's post), so the account
  // filter would wrongly drop the reblogs.
  if (view.kind === "profile" && view.acct && !view.includeReplies) {
    out = out.filter((t) => {
      const acc = t.op?.account || {};
      return (acc.acct || acc.username) === view.acct;
    });
  }

  if (filterText) {
    const q = filterText.toLowerCase();
    out = out.filter((t) => {
      const s = t.op || {};
      const acc = s.account || {};
      const hay = [
        stripHTML(s.content || ""),
        acc.display_name || "",
        acc.acct || "",
        acc.username || "",
      ].join("\n").toLowerCase();
      return hay.includes(q);
    });
  }

  return out;
}

export function sortCatalogThreads(threads, mode) {
  const sorted = [...threads];
  if (mode === "new") {
    sorted.sort((a, b) => compareActivityDesc(statusActivityKey(a.op), statusActivityKey(b.op)));
    return sorted;
  }
  if (mode === "old") {
    sorted.sort((a, b) => compareActivityAsc(statusActivityKey(a.op), statusActivityKey(b.op)));
    return sorted;
  }
  if (mode === "replies") {
    sorted.sort((a, b) => {
      const diff = (b.op?.replies_count || 0) - (a.op?.replies_count || 0);
      if (diff) return diff;
      return compareActivityDesc(a.bump, b.bump);
    });
    return sorted;
  }
  if (mode === "reacts") {
    sorted.sort((a, b) => {
      const diff = (b.op?.favourites_count || 0) - (a.op?.favourites_count || 0);
      if (diff) return diff;
      return compareActivityDesc(a.bump, b.bump);
    });
    return sorted;
  }

  // bump (default)
  sorted.sort((a, b) => compareActivityDesc(a.bump, b.bump));
  return sorted;
}

export function paginate(items, page, perPage) {
  const start = (page - 1) * perPage;
  return items.slice(start, start + perPage);
}

export function pageNumberFor(globalIndex, perPage) {
  return Math.floor(globalIndex / perPage) + 1;
}

// Resolve a status's subject line and the post body with that subject removed.
//
// Mitra now exposes a post's subject in the native `title` field. For backward
// compatibility it *also* prepends the title to the content HTML as a leading
// `<h1>Subject</h1>`, so when a title is present we strip that leading <h1> to
// avoid showing the subject twice.
//
// Older posts (and other servers) carry the subject only inside the content as
// a leading <h1>, or in a native `summary`/`spoiler_text` field — both are
// still handled.
//
// Returns { summary: string|null, strippedContent: string }
export function extractSummary(status) {
  if (!status) return { summary: null, strippedContent: "" };

  const content = status.content || "";

  // Match a leading <h1> tag (case-insensitive, with optional attributes).
  // We only treat it as a subject if it's the first non-whitespace content.
  const h1Re = /^\s*<h1\b[^>]*>([\s\S]*?)<\/h1>\s*/i;

  // Native `title` (Mitra): the authoritative subject. Strip the duplicate
  // leading <h1> the server mirrors into content.
  if (status.title) {
    const summary = String(status.title).replace(/<[^>]+>/g, "").trim();
    if (summary) {
      return { summary, strippedContent: content.replace(h1Re, "") };
    }
  }

  // Native summary/spoiler_text from Mastodon/Pleroma — use as-is.
  if (status.summary || status.spoiler_text) {
    return {
      summary: status.summary || status.spoiler_text || null,
      strippedContent: content,
    };
  }

  const m = content.match(h1Re);
  if (m) {
    // Strip HTML tags from the h1 inner content for plain-text summary.
    const summary = (m[1] || "").replace(/<[^>]+>/g, "").trim();
    if (summary) {
      const strippedContent = content.slice(m[0].length);
      return { summary, strippedContent };
    }
  }

  return { summary: null, strippedContent: content };
}
